import type { BillingInterval, BillingPlan } from 'definitions/billing-plans';
import { useState } from 'react';
import { BILLING_PLAN_CATALOGUE, planPeriodPrice } from 'definitions/billing-plans';
import { useBillingState } from '@/state/billing/useBillingState';
import { useCheckout } from '@/state/billing/useCheckout';
import {
  DEFAULT_TRAFFIC_INDEX,
  firstChargeDate,
  formatShortDate,
  monthlyEquivalent,
  recommendedPlan,
  yearlySaving,
} from './plan-model';

/**
 * The plan picker on a new account's first step: the traffic slider, the interval, the
 * plan chosen (following the recommendation until someone picks one) and what the trial
 * will cost and when. All of the page's state lives here; the components only draw it.
 *
 * Moving the slider resets the choice to the recommendation. Yearly is the default.
 */
export const usePlanStep = () => {
  const { billingState } = useBillingState();
  const checkout = useCheckout();

  const [interval, setInterval] = useState<BillingInterval>('year');
  const [trafficIndex, setTrafficIndexState] = useState(DEFAULT_TRAFFIC_INDEX);
  const [pickedPlan, setPickedPlan] = useState<BillingPlan | null>(null);

  const recommended = recommendedPlan(trafficIndex);
  const selected = pickedPlan ?? recommended;
  const details = BILLING_PLAN_CATALOGUE[selected];

  const trialAvailable = billingState?.trialAvailable ?? true;
  const checkoutAvailable = billingState?.checkoutAvailable ?? false;
  const now = new Date();
  const firstCharge = planPeriodPrice({ plan: selected, interval });

  const setTrafficIndex = (index: number) => {
    setTrafficIndexState(index);
    setPickedPlan(null);
  };

  return {
    interval,
    setInterval,
    trafficIndex,
    setTrafficIndex,
    recommended,
    selected,
    selectPlan: setPickedPlan,
    selectedName: details.name,
    trialAvailable,
    checkoutAvailable,
    perMonth: monthlyEquivalent({ plan: selected, interval }),
    saving: yearlySaving(selected),
    firstCharge,
    chargeDate: formatShortDate(firstChargeDate(now)),
    isStarting: checkout.isStarting,
    start: () => checkout.startCheckout({ plan: selected, interval }),
  };
};

export type PlanStepManager = ReturnType<typeof usePlanStep>;
